import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Plus,
    Database,
    FileText,
    FunctionSquare,
    ShieldCheck,
    Layers,
    ChevronDown,
    X,
    Package,
} from 'lucide-react';
import { PolicyElementCard } from './PolicyElementCard';
import { ConstantForm } from './forms/ConstantForm';
import { SingleValueRuleForm } from './forms/SingleValueRuleForm';
import { SetRuleForm } from './forms/SetRuleForm';
import { ObjectRuleForm } from './forms/ObjectRuleForm';
import { FunctionForm } from './forms/FunctionForm';
import { DecisionForm } from './forms/DecisionForm';
import { generateRego, renderElement } from './utils/rego-generator';
import type {
    PolicyDocument,
    PolicyElement,
    ConstantElement,
    SingleValueRuleElement,
    SetRuleElement,
    ObjectRuleElement,
    FunctionElement,
} from '@/types/policy-editor';
import { getElementCategory as getCat } from '@/types/policy-editor';

interface PolicyRuleEditorProps {
    document: PolicyDocument;
    onChange: (document: PolicyDocument) => void;
    readOnly?: boolean;
}

type FormType = 'constant' | 'singleValueRule' | 'setRule' | 'objectRule' | 'function' | 'decision' | null;

const TAB_META = [
    { value: 'decision', label: 'Decision Output',    icon: ShieldCheck,    accent: 'text-emerald-600' },
    { value: 'data',     label: 'Configuration Data', icon: Database,       accent: 'text-amber-600' },
    { value: 'rule',     label: 'Rules',              icon: FileText,       accent: 'text-blue-600' },
    { value: 'function', label: 'Helper Functions',   icon: FunctionSquare, accent: 'text-purple-600' },
    { value: 'raw',      label: 'Advanced',           icon: Layers,         accent: 'text-slate-500' },
] as const;

type TabKey = (typeof TAB_META)[number]['value'];

function canvasTitle(formType: FormType, isEdit: boolean): string {
    if (!formType) return '';
    const labels: Record<string, string> = {
        constant: 'Data Block',
        singleValueRule: 'Rule',
        setRule: 'Set Rule',
        objectRule: 'Object Rule',
        function: 'Function',
        decision: 'Decision Output',
    };
    return `${isEdit ? 'Edit' : 'Add'} ${labels[formType] ?? ''}`;
}

export function PolicyRuleEditor({
    document,
    onChange,
    readOnly = false,
}: PolicyRuleEditorProps) {
    const [activeTab, setActiveTab] = useState<string>('decision');
    const [activeForm, setActiveForm] = useState<FormType>(null);
    const [editingElement, setEditingElement] = useState<PolicyElement | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<PolicyElement | null>(null);

    const grouped = useMemo(() => {
        const result: Record<TabKey, PolicyElement[]> = {
            decision: [], data: [], rule: [], function: [], raw: [],
        };
        for (const el of document.elements) {
            result[getCat(el)].push(el);
        }
        return result;
    }, [document.elements]);

    const handleSaveElement = useCallback(
        (saved: PolicyElement) => {
            const idx = document.elements.findIndex((e) => e.id === saved.id);
            const next = [...document.elements];
            if (idx >= 0) { next[idx] = saved; } else { next.push(saved); }
            onChange({ ...document, elements: next });
            setEditingElement(null);
            setActiveForm(null);
        },
        [document, onChange],
    );

    const handleDeleteElement = useCallback(
        (el: PolicyElement) => {
            onChange({ ...document, elements: document.elements.filter((e) => e.id !== el.id) });
            setDeleteTarget(null);
            if (editingElement?.id === el.id) { setEditingElement(null); setActiveForm(null); }
        },
        [document, onChange, editingElement],
    );

    const handleDuplicate = useCallback(
        (el: PolicyElement) => {
            const dup = { ...JSON.parse(JSON.stringify(el)), id: `el-${Date.now()}`, name: `${el.name}_copy` };
            onChange({ ...document, elements: [...document.elements, dup] });
        },
        [document, onChange],
    );

    const openEdit = useCallback((el: PolicyElement) => {
        setEditingElement(el);
        if (getCat(el) === 'decision') {
            if (el.name === 'decision') {
                setActiveForm('decision');
            } else {
                setActiveForm(el.elementType === 'rawRego' ? null : el.elementType as FormType);
            }
        } else {
            setActiveForm(el.elementType === 'rawRego' ? null : el.elementType as FormType);
        }
    }, []);

    const openAdd = useCallback((type: FormType) => {
        setEditingElement(null);
        setActiveForm(type);
    }, []);

    const closeCanvas = useCallback(() => {
        setEditingElement(null);
        setActiveForm(null);
    }, []);

    const canvasHasContent = activeForm !== null;

    const elementPreview = useMemo(() => {
        if (!editingElement) return null;
        try { return renderElement(editingElement); }
        catch { return null; }
    }, [editingElement]);

    const handlePackageChange = useCallback((newPath: string) => {
        onChange({ ...document, packagePath: newPath });
    }, [document, onChange]);

    const generatedRego = useMemo(() => {
        try { return generateRego(document); }
        catch { return '// Error generating Rego'; }
    }, [document]);

    return (
        <div className="flex h-full w-full gap-0">
            {/* Left panel: tabs + card grid (~3/8 width) */}
            <div className="flex w-1/2 min-w-0 flex-col overflow-y-auto">
                {/* Package & imports header */}
                {document.packagePath && (
                    <div className="mx-2 mt-2 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/80 px-2.5 py-1.5">
                        <Package size={12} className="shrink-0 text-slate-400" />
                        {readOnly ? (
                            <span className="truncate font-mono text-[11px] text-slate-600">{document.packagePath}</span>
                        ) : (
                            <Input
                                className="h-6 border-0 bg-transparent p-0 font-mono text-[11px] text-slate-600 shadow-none focus-visible:ring-0"
                                value={document.packagePath}
                                onChange={(e) => handlePackageChange(e.target.value)}
                            />
                        )}
                        {document.imports.length > 0 && (
                            <span className="shrink-0 text-[10px] text-slate-400">
                                {document.imports.map(i => i.alias ? `${i.path} as ${i.alias}` : i.path).join(', ')}
                            </span>
                        )}
                    </div>
                )}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full flex-col">
                    <TabsList className="mx-2 mt-2 mb-1 w-auto justify-start gap-0.5 bg-slate-100/70 p-0.5">
                        {TAB_META.map((tab) => {
                            const count = grouped[tab.value].length;
                            const Icon = tab.icon;
                            return (
                                <TabsTrigger
                                    key={tab.value}
                                    value={tab.value}
                                    className="gap-1 px-2 py-1 text-[11px] data-[state=active]:shadow-sm"
                                >
                                    <Icon size={12} className={tab.accent} />
                                    <span>{tab.label}</span>
                                    {count > 0 && (
                                        <span className="rounded-full bg-slate-200/80 px-1.5 text-[9px] font-medium text-slate-600">
                                            {count}
                                        </span>
                                    )}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    <div className="flex-1 overflow-y-auto">
                        <TabsContent value="decision" className="mt-0 px-2 pb-2">
                            <CardGrid
                                elements={grouped.decision}
                                emptyLabel="No decision rules defined yet"
                                emptyHint="Decision outputs (allow/deny) define the primary result of this policy."
                                readOnly={readOnly}
                                category="decision"
                                selectedId={editingElement?.id}
                                onEdit={openEdit}
                                onDelete={setDeleteTarget}
                                onDuplicate={handleDuplicate}
                            />
                            {!readOnly && (
                                <div className="mt-2 flex gap-2">
                                    <AddButton label="Add allow/deny rule" onClick={() => openAdd('singleValueRule')} accent="emerald" />
                                    <AddButton label="Add structured decision" onClick={() => openAdd('decision')} accent="emerald" />
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="data" className="mt-0 px-2 pb-2">
                            <CardGrid
                                elements={grouped.data}
                                emptyLabel="No configuration data defined yet"
                                emptyHint="Data blocks define constants and default values used by rules."
                                readOnly={readOnly}
                                category="data"
                                selectedId={editingElement?.id}
                                onEdit={openEdit}
                                onDelete={setDeleteTarget}
                                onDuplicate={handleDuplicate}
                            />
                            {!readOnly && (
                                <div className="mt-2">
                                    <AddButton label="Add data block" onClick={() => openAdd('constant')} accent="amber" />
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="rule" className="mt-0 px-2 pb-2">
                            <CardGrid
                                elements={grouped.rule}
                                emptyLabel="No rules defined yet"
                                emptyHint="Rules define conditional logic: single-value rules, set collectors, and object builders."
                                readOnly={readOnly}
                                category="rule"
                                selectedId={editingElement?.id}
                                onEdit={openEdit}
                                onDelete={setDeleteTarget}
                                onDuplicate={handleDuplicate}
                            />
                            {!readOnly && (
                                <div className="mt-2">
                                    <RuleAddMenu onAdd={openAdd} />
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="function" className="mt-0 px-2 pb-2">
                            <CardGrid
                                elements={grouped.function}
                                emptyLabel="No helper functions defined yet"
                                emptyHint="Functions are reusable logic blocks that can be called from rules."
                                readOnly={readOnly}
                                category="function"
                                selectedId={editingElement?.id}
                                onEdit={openEdit}
                                onDelete={setDeleteTarget}
                                onDuplicate={handleDuplicate}
                            />
                            {!readOnly && (
                                <div className="mt-2">
                                    <AddButton label="Add function" onClick={() => openAdd('function')} accent="purple" />
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="raw" className="mt-0 px-2 pb-2">
                            {grouped.raw.length > 0 && (
                                <CardGrid
                                    elements={grouped.raw}
                                    emptyLabel="No raw Rego blocks"
                                    readOnly
                                    selectedId={editingElement?.id}
                                    onEdit={() => {}}
                                    onDelete={setDeleteTarget}
                                    onDuplicate={handleDuplicate}
                                />
                            )}
                            <div className="mt-2">
                                <p className="mb-1.5 text-[11px] font-medium text-slate-500">Generated Rego Preview</p>
                                <pre className="max-h-[400px] overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-[11px] leading-relaxed text-slate-700 whitespace-pre-wrap">{generatedRego}</pre>
                            </div>
                        </TabsContent>
                    </div>
                </Tabs>
            </div>

            {/* Right canvas: always-visible editing panel (~5/8 width) */}
            <div className="flex w-1/2 min-w-[320px] flex-col border-l bg-white">
                {canvasHasContent ? (
                    <>
                        <div className="flex items-center justify-between border-b px-4 py-2.5">
                            <h3 className="text-sm font-semibold text-slate-800">
                                {canvasTitle(activeForm, !!editingElement)}
                            </h3>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={closeCanvas}>
                                <X size={14} />
                            </Button>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {activeForm === 'constant' && (
                                <ConstantForm
                                    element={editingElement?.elementType === 'constant' ? editingElement as ConstantElement : null}
                                    onSave={handleSaveElement}
                                    onCancel={closeCanvas}
                                />
                            )}
                            {activeForm === 'singleValueRule' && (
                                <SingleValueRuleForm
                                    element={editingElement?.elementType === 'singleValueRule' ? editingElement as SingleValueRuleElement : null}
                                    onSave={handleSaveElement}
                                    onCancel={closeCanvas}
                                />
                            )}
                            {activeForm === 'setRule' && (
                                <SetRuleForm
                                    element={editingElement?.elementType === 'setRule' ? editingElement as SetRuleElement : null}
                                    onSave={handleSaveElement}
                                    onCancel={closeCanvas}
                                />
                            )}
                            {activeForm === 'objectRule' && (
                                <ObjectRuleForm
                                    element={editingElement?.elementType === 'objectRule' ? editingElement as ObjectRuleElement : null}
                                    onSave={handleSaveElement}
                                    onCancel={closeCanvas}
                                />
                            )}
                            {activeForm === 'function' && (
                                <FunctionForm
                                    element={editingElement?.elementType === 'function' ? editingElement as FunctionElement : null}
                                    onSave={handleSaveElement}
                                    onCancel={closeCanvas}
                                />
                            )}
                            {activeForm === 'decision' && (
                                <DecisionForm
                                    element={
                                        editingElement?.elementType === 'singleValueRule' && editingElement.name === 'decision'
                                            ? editingElement as SingleValueRuleElement
                                            : null
                                    }
                                    onSave={handleSaveElement}
                                    onCancel={closeCanvas}
                                />
                            )}

                            {elementPreview && (
                                <ElementPreview rego={elementPreview} />
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <p className="text-center text-sm text-slate-400">
                            Select an element to edit, or add a new one
                        </p>
                    </div>
                )}
            </div>

            {/* Delete confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete element?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove <strong>{deleteTarget?.name}</strong> from the policy.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => deleteTarget && handleDeleteElement(deleteTarget)}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function CardGrid({
    elements,
    emptyLabel,
    emptyHint,
    readOnly,
    category,
    selectedId,
    onEdit,
    onDelete,
    onDuplicate,
}: {
    elements: PolicyElement[];
    emptyLabel: string;
    emptyHint?: string;
    readOnly: boolean;
    category?: import('./PolicyElementCard').CardCategory;
    selectedId?: string;
    onEdit: (el: PolicyElement) => void;
    onDelete: (el: PolicyElement) => void;
    onDuplicate: (el: PolicyElement) => void;
}) {
    if (elements.length === 0) {
        return (
            <div className="py-6 text-center">
                <p className="text-xs text-slate-400">{emptyLabel}</p>
                {emptyHint && <p className="mt-1 text-[11px] text-slate-300">{emptyHint}</p>}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
            {elements.map((el) => (
                <PolicyElementCard
                    key={el.id}
                    element={el}
                    category={category}
                    selected={el.id === selectedId}
                    onEdit={() => onEdit(el)}
                    onDelete={() => onDelete(el)}
                    onDuplicate={() => onDuplicate(el)}
                    readOnly={readOnly}
                />
            ))}
        </div>
    );
}

function AddButton({ label, onClick, accent }: { label: string; onClick: () => void; accent?: string }) {
    const accentMap: Record<string, string> = {
        emerald: 'text-emerald-600 border-emerald-200 hover:bg-emerald-50',
        amber:   'text-amber-600 border-amber-200 hover:bg-amber-50',
        blue:    'text-blue-600 border-blue-200 hover:bg-blue-50',
        purple:  'text-purple-600 border-purple-200 hover:bg-purple-50',
    };
    const colorClasses = accent ? accentMap[accent] ?? '' : '';
    return (
        <Button variant="outline" size="sm" className={`h-7 text-[11px] ${colorClasses}`} onClick={onClick}>
            <Plus size={11} className="mr-1" />
            {label}
        </Button>
    );
}

function RuleAddMenu({ onAdd }: { onAdd: (type: FormType) => void }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-[11px] text-blue-600 border-blue-200 hover:bg-blue-50">
                    <Plus size={11} className="mr-1" />
                    Add rule
                    <ChevronDown size={11} className="ml-1" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => onAdd('singleValueRule')}>Single-value rule</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAdd('setRule')}>Set rule</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAdd('objectRule')}>Object rule</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function ElementPreview({ rego }: { rego: string }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border-t border-slate-100 bg-slate-50/50">
            <button
                className="flex w-full items-center justify-between px-4 py-2 text-[11px] font-medium text-slate-500 hover:text-slate-700"
                onClick={() => setExpanded(!expanded)}
            >
                <span>Rego Preview</span>
                <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
            {expanded && (
                <pre className="max-h-[200px] overflow-auto border-t border-slate-100 px-4 py-2 font-mono text-[11px] leading-relaxed text-slate-600 whitespace-pre-wrap">
                    {rego}
                </pre>
            )}
        </div>
    );
}
